import {Fragment, ReactElement, useState} from 'react'
import {withNamespaces, WithNamespaces, Trans} from 'react-i18next'
import {
  Typography, Grid, List, ListItem, ListItemText, Divider,
  Collapse, IconButton, Table, TableHead, TableBody, TableRow, TableCell,
} from '@material-ui/core'
import {KeyboardArrowDown, KeyboardArrowUp} from '@material-ui/icons'
import messages from '../../../../../../../assets/messages'
import {formatDate} from '../../../../../../../common/utils'
import Amount from '../../../../../../Common/Amount'
import {TablePropsBase} from '../../types'
import {PaidPerformanceAmount} from '../PaidPerformanceAmount'
import {useStyles} from './styles'

const Component = (props: TablePropsBase & WithNamespaces): ReactElement => {
  const {lng, subscriptions, isDefaultDate, children} = props
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set())
  const classes = useStyles()

  const toggleExpanded = (id: number) => {
    setExpandedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  return (
    <List classes={{root: classes.container}}>
      {subscriptions.map(subscription => {
        const {
          id,
          followerFullName,
          startedAt,
          stoppedAt,
          profit,
          pendingPerformanceFee,
          currency,
          settings,
        } = subscription
        const isExpanded = expandedIds.has(id)

        return (
          <Fragment key={id}>
            <ListItem dense classes={{root: classes.listItem}}>
              <ListItemText classes={{root: classes.listItem}}>
                <Grid container justifyContent="space-between" direction="row">
                  <Grid item>
                    <Typography variant="caption">
                      <Trans {...messages.clientName} />
                    </Typography>
                  </Grid>
                  <Grid item>
                    <Typography variant="body1">
                      {followerFullName ?? '-'}
                    </Typography>
                  </Grid>
                </Grid>
                <Grid container justifyContent="space-between" direction="row">
                  <Grid item>
                    <Typography variant="caption">
                      <Trans {...messages.startCopyingDate} />
                    </Typography>
                  </Grid>
                  <Grid item>
                    <Typography variant="body1">
                      {startedAt != null ? formatDate(lng, startedAt) : '-'}
                    </Typography>
                  </Grid>
                </Grid>
                <Grid container justifyContent="space-between" direction="row">
                  <Grid item>
                    <Typography variant="caption">
                      <Trans {...messages.profitPerPeriod} />
                    </Typography>
                  </Grid>
                  <Grid item>
                    <Typography variant="body1">
                      <Amount value={profit} currency={currency} locale='en-US' />
                    </Typography>
                  </Grid>
                </Grid>
                <Grid container justifyContent="space-between" direction="row">
                  <Grid item>
                    <Typography variant="caption">
                      <Trans {...messages.stopCopyingDate} />
                    </Typography>
                  </Grid>
                  <Grid item>
                    <Typography variant="body1">
                      {stoppedAt != null ? formatDate(lng, stoppedAt) : <Trans {...messages.copying} />}
                    </Typography>
                  </Grid>
                </Grid>
                <Grid container justifyContent="space-between" direction="row">
                  <Grid item>
                    <Typography variant="caption">
                      <Trans {...messages.performanceFeePaid} />
                    </Typography>
                  </Grid>
                  <Grid item>
                    <Typography variant="body1">
                      <PaidPerformanceAmount subscription={subscription} isDefaultDate={isDefaultDate} />
                    </Typography>
                  </Grid>
                </Grid>
                {isDefaultDate ? (
                  <Grid container justifyContent="space-between" direction="row">
                    <Grid item>
                      <Typography variant="caption">
                        <Trans {...messages.performanceFee} />
                      </Typography>
                    </Grid>
                    <Grid item>
                      <Typography variant="body1">
                        <Amount
                          value={pendingPerformanceFee}
                          currency={currency}
                          locale='en-US'
                          classes={{root: classes.yellowText}}
                        />
                      </Typography>
                    </Grid>
                  </Grid>
                ) : null}
                {settings != null && (
                  <Grid container justifyContent="space-between" direction="row" alignItems="center">
                    <Grid item>
                      <Typography variant="caption">
                        <Trans {...messages.advancedSettings} />
                      </Typography>
                    </Grid>
                    <Grid item>
                      <IconButton size="small" onClick={() => toggleExpanded(id)}>
                        {isExpanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                      </IconButton>
                    </Grid>
                  </Grid>
                )}
                {settings != null && (
                  <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                    <Grid container direction="column" style={{paddingTop: 8}}>
                      <Typography variant="caption" style={{fontWeight: 600}}>
                        <Trans {...messages.strategySettings} />
                      </Typography>
                      <Grid container justifyContent="space-between">
                        <Typography variant="caption"><Trans {...messages.strategyType} /></Typography>
                        <Typography variant="body2">{settings.strategy.type}</Typography>
                      </Grid>
                      <Grid container justifyContent="space-between">
                        <Typography variant="caption"><Trans {...messages.strategyDirection} /></Typography>
                        <Typography variant="body2">{settings.strategy.direction}</Typography>
                      </Grid>
                      <Grid container justifyContent="space-between">
                        <Typography variant="caption"><Trans {...messages.strategyFundsType} /></Typography>
                        <Typography variant="body2">{settings.strategy.fundsType}</Typography>
                      </Grid>
                      <Grid container justifyContent="space-between">
                        <Typography variant="caption"><Trans {...messages.strategyRiskRatio} /></Typography>
                        <Typography variant="body2">{settings.strategy.riskRatio}</Typography>
                      </Grid>
                      <Grid container justifyContent="space-between">
                        <Typography variant="caption"><Trans {...messages.strategyLotsStep} /></Typography>
                        <Typography variant="body2">{settings.strategy.lotsStep}</Typography>
                      </Grid>
                      <Grid container justifyContent="space-between">
                        <Typography variant="caption"><Trans {...messages.strategyMinVolumeHandling} /></Typography>
                        <Typography variant="body2">{settings.strategy.minVolumeHandling}</Typography>
                      </Grid>

                      <Typography variant="caption" style={{fontWeight: 600, marginTop: 8}}>
                        <Trans {...messages.correctionSettings} />
                      </Typography>
                      <Grid container justifyContent="space-between">
                        <Typography variant="caption"><Trans {...messages.correctionRiskFactor} /></Typography>
                        <Typography variant="body2">{settings.correction.riskFactor ?? '-'}</Typography>
                      </Grid>
                      <Grid container justifyContent="space-between">
                        <Typography variant="caption"><Trans {...messages.correctionAction} /></Typography>
                        <Typography variant="body2">{settings.correction.action}</Typography>
                      </Grid>

                      {settings.riskRules.length > 0 && (
                        <>
                          <Typography variant="caption" style={{fontWeight: 600, marginTop: 8}}>
                            <Trans {...messages.riskRules} />
                          </Typography>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell style={{padding: '4px', fontSize: '0.7rem'}}><Trans {...messages.riskRuleParameter} /></TableCell>
                                <TableCell style={{padding: '4px', fontSize: '0.7rem'}}><Trans {...messages.riskRuleThreshold} /></TableCell>
                                <TableCell style={{padding: '4px', fontSize: '0.7rem'}}><Trans {...messages.riskRuleSubscriptionAction} /></TableCell>
                                <TableCell style={{padding: '4px', fontSize: '0.7rem'}}><Trans {...messages.riskRuleTradingAction} /></TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {settings.riskRules.map((rule, idx) => (
                                <TableRow key={idx}>
                                  <TableCell style={{padding: '4px', fontSize: '0.7rem'}}>{rule.parameter}</TableCell>
                                  <TableCell style={{padding: '4px', fontSize: '0.7rem'}}>{rule.threshold}</TableCell>
                                  <TableCell style={{padding: '4px', fontSize: '0.7rem'}}>{rule.subscriptionAction}</TableCell>
                                  <TableCell style={{padding: '4px', fontSize: '0.7rem'}}>{rule.tradingAction}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </>
                      )}
                    </Grid>
                  </Collapse>
                )}
              </ListItemText>
            </ListItem>
            <Divider />
          </Fragment>
        )
      })}
      <ListItem classes={{root: classes.listPaginationItem}}>
        {children}
      </ListItem>
    </List>
  )
}

export const TableMobile = withNamespaces()(Component)
